import { DSErrorComponent } from '@akso/shuffle-ds';
import { Routes } from '@angular/router';
import { PrmApiService } from 'src/app/prm/prm-api.service';
import { DistributionApiService } from 'src/app/subscription-matrix/distribution-api.service';
import { environment } from '../environments/environment';
import { SigninCallbackComponent } from './authentication/callback/signin-callback.component';
import { NotFoundComponent } from './authentication/not-found/not-found.component';
import { SilentCallbackComponent } from './authentication/silent-callback/silent-callback.component';
import { authGuard } from './core/guards/auth.guard';
import { featureToggleGuard } from './core/guards/feature-toggle.guard';
import { projectIdResolver } from './core/guards/projectid.resolver';
import { projectsResolver } from './core/guards/projects.resolver';
import { canActivateUserpilotReload } from './shared/userpilot/guards/reload-userpilot.guard';
import { ShellComponent } from './shell/shell.component';

// projects/{projectid}/
const projectRoutes: Routes = [
  {
    path: 'projectlist',
    title: 'Projects - IX',
    loadComponent: () => import('src/app/projects/projects.component').then(m => m.ProjectsComponent),
  },
  {
    path: 'usersetup',
    title: 'User setup - IX',
    loadComponent: () => import('src/app/user-setup/user-setup.component').then(m => m.UserSetupComponent),
  },
  {
    path: 'projectsetup',
    title: 'Project setup - IX',
    loadChildren: () => import('src/app/project-setup/project-setup.routes'),
  },
  {
    path: 'units',
    title: 'Units - IX',
    loadComponent: () => import('src/app/units/units.component').then(m => m.UnitsComponent),
  },
  {
    path: 'tagadministration',
    title: 'Tag administration - IX',
    loadComponent: () =>
      import('src/app/tag-administration/tag-administration.component').then(
        m => m.TagAdministrationComponent,
      ),
  },
  {
    path: 'transmittals',
    title: 'Transmittals - IX',
    loadComponent: () =>
      import('src/app/transmittals/transmittals.component').then(m => m.TransmittalsComponent),
  },
  {
    path: 'review-deprecated', // TODO: Delete this route when the old review page is removed
    title: 'Old Review - IX',
    loadChildren: () => import('src/app/review-deprecated/review.routes'),
    canMatch: [featureToggleGuard],
    data: { featureFlagKey: 'OldReviewPage' },
  },
  {
    path: 'review',
    title: 'Review - IX',
    loadChildren: () => import('src/app/review/review.routes'),
  },
  {
    path: 'prm',
    title: 'PRM - IX',
    providers: [PrmApiService],
    loadComponent: () => import('src/app/prm/prm.component').then(m => m.PrmComponent),
  },
  {
    path: 'distribution-matrix',
    title: 'Subscription matrix - IX',
    providers: [DistributionApiService],
    loadComponent: () =>
      import('src/app/subscription-matrix/distribution-matrix.component').then(
        m => m.DistributionMatrixComponent,
      ),
  },
  {
    path: 'deliverables',
    title: 'Deliverables admin - IX',
    loadComponent: () =>
      import('src/app/deliverables-admin/deliverables-admin.component').then(
        m => m.DeliverablesAdminComponent,
      ),
  },
  {
    path: 'deliverables-status',
    title: 'Deliverables status - IX',
    loadChildren: () => import('src/app/deliverables-status/deliverables-status.routes'),
  },
  {
    path: 'notifications',
    title: 'Notifications - IX',
    loadComponent: () =>
      import('src/app/notifications/notifications.component').then(m => m.NotificationsComponent),
  },
  {
    path: 'sidrl',
    title: 'SIDRL - IX',
    loadChildren: () => import('src/app/sidrl/sidrl.routes'),
    canMatch: [featureToggleGuard],
    data: { featureFlagKey: 'SIDRL' },
  },
  {
    path: 'transactions-monitor',
    title: 'Transactions monitor - IX',
    loadComponent: () =>
      import('src/app/transactions-monitor/transactions-monitor.component').then(
        m => m.TransactionsMonitorComponent,
      ),
  },
  {
    path: 'batch-download',
    title: 'Batch download docs - IX',
    loadChildren: () => import('src/app/batch-download/batch-download.routes'),
    canMatch: [featureToggleGuard],
    data: { featureFlagKey: 'BatchDownload' },
  },
  {
    path: '**',
    loadChildren: () => import('src/app/dashboard/dashboard.routes'),
  },
];

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'projects',
    pathMatch: 'full',
    canActivateChild: [canActivateUserpilotReload],
    title: 'Information Exchange',
  },
  {
    path: 'projects',
    component: ShellComponent,
    canActivateChild: [authGuard],
    resolve: { projectsResolver },
    children: [
      {
        path: ':projectId',
        resolve: { projectIdResolver },
        children: projectRoutes,
      },
      {
        path: '**',
        loadChildren: () => import('src/app/dashboard/dashboard.routes'),
      },
    ],
  },
  {
    path: 'alias',
    title: 'Alias - IX',
    component: ShellComponent,
    canActivate: [authGuard],
    children: environment.trimAlias
      ? [
          {
            path: '',
            loadComponent: () =>
              import('src/app/authentication/alias/alias.component').then(m => m.AliasComponent),
          },
        ]
      : [],
  },
  { path: 'error/:id', component: DSErrorComponent },
  {
    path: 'access-denied',
    pathMatch: 'full',
    loadComponent: () =>
      import('src/app/authentication/access-denied/access-denied.component').then(
        m => m.AccessDeniedComponent,
      ),
  },
  { path: 'not-found', pathMatch: 'full', component: NotFoundComponent },
  { path: 'invalid', pathMatch: 'full', component: NotFoundComponent },
  { path: 'silent-callback', component: SilentCallbackComponent },
  { path: 'signin-callback', component: SigninCallbackComponent },
  { path: '**', component: NotFoundComponent },
];
